import { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from 'react'
import {
  LayoutDashboard, CheckSquare, Users, Building2, Star,
  MessageSquare, LogOut, Plus, Download, X, Trash2, ClipboardCheck, NotebookPen, RefreshCw, Layers, GitMerge, UserCheck, Shield, Ticket, AlertTriangle, PenLine, Archive, RotateCcw, Smartphone, Search, Globe, ClipboardList, Clock, BarChart3, BookOpen, TrendingUp, Sprout, Mic, DollarSign
} from 'lucide-react'
import { supabase } from './supabase'
import { Auth } from '@supabase/auth-ui-react'
import { ThemeSupa } from '@supabase/auth-ui-shared'
import { SERVICE_AREAS, STATUSES, ROLES, DONE_STATUSES, getServiceArea, getStatus, REVISION_STATUSES, isActiveProfile, CLIENT_APPROVED_STATUSES, BLOG_PUBLISH_WARNING } from './constants'
import { DEFAULT_FILTERS, loadFilters, saveFilters, clearSavedFilters, activeFilterCount } from './taskFilters'
import { useToast } from './ToastContext'
import TaskModal from './TaskModal'
import TaskDetail from './TaskDetail'
import OnboardingSpotlight from './OnboardingSpotlight'
import { format, formatDistanceToNow } from 'date-fns'
import MeetingNotesView from './MeetingNotes'
import RecurringTasksView from './RecurringTasks'
import ProjectsView from './Projects'
import TicketDeskView from './TicketDesk'
import ClientProfile from './ClientProfile'
import ClientProfileFull from './ClientProfileFull'
import InventoryView from './InventoryView'
import SEOWorkspaceView from './SEOWorkspace'
import WebsiteWorkspaceView from './WebsiteWorkspace'
import { computeVirtualSeoTasks } from './seoVirtualTasks'
import { computeVirtualWebsiteTasks } from './websiteVirtualTasks'

import BulkTaskModal from './BulkTaskModal'
import BlogAssignmentsView from './BlogAssignments'
import SocialBatchesView from './SocialBatches'
import TimeTrackingView from './TimeTracking'
import { TimerProvider, ActiveTimerBar, RunawayTimerReminder } from './TaskTimer'
import TimeReportView from './TimeReport'
import ResourcesView from './Resources'
import KickoffBuilder from './KickoffBuilder'
import WorkflowBuilder from './WorkflowBuilder'
import { SERVICES, platformsForServices, generateOnboardingToken, onboardingUrlFor } from './onboardingConfig'
import ADScorecardView from './ADScorecard'
import { canViewADScorecard } from './adScorecardConfig'
import First90View from './First90'
import KickoffDecksView from './KickoffDecks'
import CommissionView from './Commission'
import { canViewCommission, commissionFor, dealWindow, monthLabel, fmtMoney } from './commissionConfig'

// Supabase caps how many rows a single request returns ("Max rows" in
// Settings → API). A plain select silently drops everything past the cap, so
// once the tasks table outgrew it, the oldest tasks vanished from All Tasks.
// Fetch in pages until a short page comes back. TASK_PAGE_SIZE must be at or
// below the project's Max rows setting, or the loop will stop early.
const TASK_PAGE_SIZE = 500
async function fetchAllActiveTasks() {
  const rows = []
  for (let from = 0; ; from += TASK_PAGE_SIZE) {
    const { data, error } = await supabase
      .from('tasks')
      .select('*, comment_count:task_comments(count)')
      .is('archived_at', null)
      .order('created_at', { ascending: false })
      .order('id', { ascending: true }) // tie-breaker so pages don't overlap or skip
      .range(from, from + TASK_PAGE_SIZE - 1)
    if (error) return { data: rows, error }
    rows.push(...(data || []))
    if (!data || data.length < TASK_PAGE_SIZE) break
  }
  return { data: rows, error: null }
}

const safeDate = (d, fmt) => {
  if (!d) return null
  const match = String(d).match(/^(\d{4}-\d{2}-\d{2})/)
  if (!match) return null
  try {
    const date = new Date(match[1] + 'T12:00:00')
    if (isNaN(date.getTime())) return null
    return format(date, fmt)
  } catch { return null }
}
const isBeforeToday = (d) => {
  if (!d) return false
  const match = String(d).match(/^(\d{4}-\d{2}-\d{2})/)
  if (!match) return false
  try { return new Date(match[1] + 'T12:00:00') < new Date() } catch { return false }
}

const DUE_FILTER_LABELS = { overdue: 'Overdue', today: 'Today', week: 'Next 7 days', none: 'No due date' }
const dueDateOf = (d) => {
  if (!d) return null
  const match = String(d).match(/^(\d{4}-\d{2}-\d{2})/)
  if (!match) return null
  const date = new Date(match[1] + 'T12:00:00')
  return isNaN(date.getTime()) ? null : date
}
const isDueToday = (d) => {
  const date = dueDateOf(d)
  if (!date) return false
  const now = new Date()
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()
}
// Due between now and N days out (inclusive of today, excludes already-overdue)
const isDueWithinDays = (d, days) => {
  const date = dueDateOf(d)
  if (!date) return false
  const start = new Date(); start.setHours(0, 0, 0, 0)
  const end = new Date(start); end.setDate(end.getDate() + days)
  return date >= start && date <= end
}

function AuthPage({ deactivated }) {
  return (
    <div className="auth-page">
      <div className="auth-box">
        <div className="auth-logo">LMR</div>
        <div className="auth-subtitle">Task Management · Sign in to continue</div>
        {deactivated && (
          <div style={{ fontSize: 13, background: 'var(--cream-dark)', color: 'var(--text-secondary)', padding: '10px 14px', borderRadius: 10, margin: '12px 0' }}>
            This account has been deactivated. Contact your Supervising AD if you need access.
          </div>
        )}
        <Auth
          supabaseClient={supabase}
          appearance={{ theme: ThemeSupa, variables: { default: { colors: { brand: '#2e3a59', brandAccent: '#c4962a' } } } }}
          providers={['google']}
          onlyThirdPartyProviders={false}
        />
      </div>
    </div>
  )
}

// Set of workflows whose step 3 (client_review) task is NOT yet client-approved,
// keyed 'blog:<blog_assignment_id>' or 'social:<social_batch_id>'. Provided by
// App so every TaskRow can flag step 4 (blog publish / social schedule) tasks.
const BlogsAwaitingClientContext = createContext(new Set())

function TaskRow({ task, profiles, clients, onClick, onDelete }) {
  const blogsAwaitingClient = useContext(BlogsAwaitingClientContext)
  const showClientWarning = !DONE_STATUSES.includes(task.status) && (
    (task.blog_phase === 'publish_checklist' && blogsAwaitingClient.has('blog:' + task.blog_assignment_id)) ||
    (task.social_phase === 'schedule' && blogsAwaitingClient.has('social:' + task.social_batch_id))
  )
  const sa = getServiceArea(task.service_area)
  const st = getStatus(task.status)
  const assignee = profiles.find(p => p.id === task.assigned_to)
  const client = clients.find(c => c.id === task.client_id)
  const isOverdue = isBeforeToday(task.date_due) && !DONE_STATUSES.includes(task.status)

  return (
    <tr onClick={() => onClick(task)}>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {task.is_urgent && <span style={{ background: 'var(--red)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 50, flexShrink: 0 }}>🔴</span>}
          {task.blog_assignment_id && <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 50, background: '#f9edf5', color: 'var(--mauve)', fontWeight: 700, flexShrink: 0 }}>✍️</span>}
          {task.social_batch_id && <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 50, background: '#e8f5fa', color: '#2a6e84', fontWeight: 700, flexShrink: 0 }} title="Social batch">📱</span>}
          {task.kickoff_id && <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 50, background: '#fdf6e3', color: 'var(--gold)', fontWeight: 700, flexShrink: 0 }} title="Generated by Kickoff Builder">🚀</span>}
          <div style={{ fontWeight: task.is_urgent ? 700 : 500, fontSize: 13.5 }}>{task.title}</div>
          {task.comment_count?.[0]?.count > 0 && (
            <span style={{ background: 'var(--lightblue)', color: 'var(--navy)', fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 50, flexShrink: 0 }}>
              💬 {task.comment_count[0].count}
            </span>
          )}
        </div>
        {task.service_subarea && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{task.service_subarea}</div>}
        {showClientWarning && (
          <div role="alert" style={{ marginTop: 6, padding: '6px 10px', background: 'var(--red)', color: '#fff', borderRadius: 8, fontSize: 11.5, fontWeight: 700, lineHeight: 1.4 }}>
            ⚠ {BLOG_PUBLISH_WARNING}
          </div>
        )}
      </td>
      <td><span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{client?.name || '—'}</span></td>
      <td>
        {sa && <span className="badge" style={{ background: sa.color + '18', color: sa.color }}>
          <span className="service-dot" style={{ background: sa.color }} />{sa.label}
        </span>}
      </td>
      <td><span style={{ fontSize: 13 }}>{assignee?.full_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</span></td>
      <td>
        <span style={{ fontSize: 13, color: isOverdue ? 'var(--urgent)' : 'var(--text-secondary)', fontWeight: isOverdue ? 600 : 400 }}>
          {safeDate(task.date_due, 'MMM d') || '—'}
        </span>
      </td>
      <td>
        {st && <span className="status-pill" style={{ background: st.bg, color: st.color }}>
          <span style={{ marginRight: 4 }}>{st.icon}</span>{st.label}
        </span>}
      </td>
      <td>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {task.is_onboarding_task && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, background: '#fdf6e3', color: 'var(--gold)', fontWeight: 600 }}>ONB</span>}
          {task.client_visible && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, background: 'var(--teal-light)', color: 'var(--teal)', fontWeight: 600 }}>CLIENT</span>}
          <button
            onClick={e => { e.stopPropagation(); onDelete(task) }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px', display: 'flex', borderRadius: 4, marginLeft: 4 }}
            title="Delete task"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  )
}

function TasksView({ tasks, profiles, clients, currentUser, onTaskClick, onDeleteTask, forceGroupBy, preset, onPresetApplied }) {
  const [filters, setFilters] = useState(() => loadFilters())
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)

  // Persist on every change so filters survive navigating away and refreshing.
  useEffect(() => { saveFilters(filters) }, [filters])

  // A preset arrives from the Dashboard ("show me my 5 in-progress tasks").
  // It replaces the current filters wholesale so what you land on matches the
  // number you clicked.
  useEffect(() => {
    if (!preset) return
    setFilters(f => ({ ...DEFAULT_FILTERS, groupBy: f.groupBy, ...preset.filters }))
    onPresetApplied && onPresetApplied()
  }, [preset]) 

  const set = (key, value) => setFilters(f => ({ ...f, [key]: value }))
  const resetFilters = () => { clearSavedFilters(); setFilters({ ...DEFAULT_FILTERS, groupBy: filters.groupBy }) }
  const toggleStatus = (val) => setFilters(f => ({
    ...f,
    filterStatuses: f.filterStatuses.includes(val)
      ? f.filterStatuses.filter(s => s !== val)
      : [...f.filterStatuses, val]
  }))

  const {
    search, filterClient, filterAssignee, filterStatuses, filterArea, filterOnboarding,
    filterCollaborator, filterAssigner, hideCompleted, showUrgentOnly, dueFilter, sortBy
  } = filters
  const groupBy = forceGroupBy || filters.groupBy
  const activeCount = activeFilterCount(filters)

  const filtered = tasks.filter(t => {
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false
    if (filterClient && t.client_id !== filterClient) return false
    if (filterAssignee && t.assigned_to !== filterAssignee) return false
    if (filterStatuses.length > 0 && !filterStatuses.includes(t.status)) return false
    if (filterArea && t.service_area !== filterArea) return false
    if (filterOnboarding && !t.is_onboarding_task) return false
    if (filterCollaborator && !(t.collaborator_ids || []).includes(filterCollaborator)) return false
    if (filterAssigner && t.assigned_by !== filterAssigner) return false
    if (hideCompleted && DONE_STATUSES.includes(t.status)) return false
    if (showUrgentOnly && !t.is_urgent) return false
    if (dueFilter === 'overdue' && !(isBeforeToday(t.date_due) && !DONE_STATUSES.includes(t.status))) return false
    if (dueFilter === 'today' && !isDueToday(t.date_due)) return false
    if (dueFilter === 'week' && !isDueWithinDays(t.date_due, 7)) return false
    if (dueFilter === 'none' && t.date_due) return false
    return true
  }).sort((a, b) => {
    if (a.is_urgent && !b.is_urgent) return -1
    if (!a.is_urgent && b.is_urgent) return 1
    if (sortBy === 'due_asc') {
      if (!a.date_due && !b.date_due) return 0
      if (!a.date_due) return 1
      if (!b.date_due) return -1
      return (new Date(a.date_due || '9999-12-31T12:00:00') - new Date(b.date_due || '9999-12-31T12:00:00'))
    }
    if (sortBy === 'due_desc') {
      if (!a.date_due && !b.date_due) return 0
      if (!a.date_due) return 1
      if (!b.date_due) return -1
      return (new Date(b.date_due || '1970-01-01T12:00:00') - new Date(a.date_due || '1970-01-01T12:00:00'))
    }
    return 0
  })

  const grouped = {}
  if (groupBy === 'client') {
    filtered.forEach(t => {
      const c = clients.find(c => c.id === t.client_id)
      const key = c?.name || 'No Client'
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(t)
    })
  } else if (groupBy === 'assignee') {
    filtered.forEach(t => {
      const p = profiles.find(p => p.id === t.assigned_to)
      const key = p?.full_name || 'Unassigned'
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(t)
    })
  } else if (groupBy === 'status') {
    STATUSES.forEach(s => { grouped[s.label] = [] })
    filtered.forEach(t => {
      const st = getStatus(t.status)
      const key = st?.label || 'Unknown'
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(t)
    })
  } else if (groupBy === 'area') {
    filtered.forEach(t => {
      const sa = getServiceArea(t.service_area)
      const key = sa?.label || 'Other'
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(t)
    })
  } else {
    grouped['All Tasks'] = filtered
  }

  // Human-readable list of what's currently on, each removable.
  const nameOf = id => profiles.find(p => p.id === id)?.full_name || 'Unknown'
  const chips = []
  if (search) chips.push({ key: 'search', label: `Search: "${search}"`, clear: () => set('search', '') })
  if (filterClient) chips.push({ key: 'client', label: `Client: ${clients.find(c => c.id === filterClient)?.name || 'Unknown'}`, clear: () => set('filterClient', '') })
  if (filterAssignee) chips.push({ key: 'assignee', label: `Assigned to: ${filterAssignee === currentUser.id ? 'Me' : nameOf(filterAssignee)}`, clear: () => set('filterAssignee', '') })
  if (filterAssigner) chips.push({ key: 'assigner', label: `Assigned by: ${filterAssigner === currentUser.id ? 'Me' : nameOf(filterAssigner)}`, clear: () => set('filterAssigner', '') })
  if (filterCollaborator) chips.push({ key: 'collab', label: `Collaborator: ${nameOf(filterCollaborator)}`, clear: () => set('filterCollaborator', '') })
  filterStatuses.forEach(s => chips.push({
    key: `status-${s}`,
    label: `Status: ${getStatus(s)?.label || s}`,
    clear: () => toggleStatus(s)
  }))
  if (filterArea) chips.push({ key: 'area', label: `Area: ${getServiceArea(filterArea)?.label || filterArea}`, clear: () => set('filterArea', '') })
  if (dueFilter) chips.push({ key: 'due', label: `Due: ${DUE_FILTER_LABELS[dueFilter]}`, clear: () => set('dueFilter', '') })
  if (showUrgentOnly) chips.push({ key: 'urgent', label: 'Urgent only', clear: () => set('showUrgentOnly', false) })
  if (hideCompleted) chips.push({ key: 'hide', label: 'Hiding completed', clear: () => set('hideCompleted', false) })
  if (filterOnboarding) chips.push({ key: 'onb', label: 'Onboarding only', clear: () => set('filterOnboarding', false) })

  const onStyle = (isOn) => isOn
    ? { borderColor: 'var(--navy-mid)', background: '#eef2fa', color: 'var(--navy)', fontWeight: 600 }
    : undefined

  const TableContent = ({ rows }) => (
    <table className="task-table" style={{ width: '100%' }}>
      <thead>
        <tr><th>Task</th><th>Client</th><th>Area</th><th>Assigned To</th><th>Due</th><th>Status</th><th></th></tr>
      </thead>
      <tbody>
        {rows.length === 0
          ? <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>No tasks match your filters</td></tr>
          : rows.map(t => <TaskRow key={t.id} task={t} profiles={profiles} clients={clients} onClick={onTaskClick} onDelete={onDeleteTask} />)
        }
      </tbody>
    </table>
  )

  return (
    <div>
      <div className="filters-bar">
        <input className="search-input" placeholder="Search tasks..." value={search} onChange={e => set('search', e.target.value)} style={onStyle(!!search)} />
        <select className="filter-select" value={filterClient} onChange={e => set('filterClient', e.target.value)} style={onStyle(!!filterClient)}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="filter-select" value={filterAssignee} onChange={e => set('filterAssignee', e.target.value)} style={onStyle(!!filterAssignee)}>
          <option value="">All Team Members</option>
          <option value={currentUser.id}>Me</option>
          {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
        </select>

        <div style={{ position: 'relative' }}>
          <button
            className="filter-select"
            onClick={() => setShowStatusDropdown(s => !s)}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, minWidth: 140, ...(onStyle(filterStatuses.length > 0) || {}) }}
          >
            {filterStatuses.length === 0
              ? 'All Statuses'
              : filterStatuses.length === 1
                ? (getStatus(filterStatuses[0])?.label || '1 selected')
                : `${filterStatuses.length} statuses`}
            <span style={{ marginLeft: 'auto' }}>▾</span>
          </button>
          {showStatusDropdown && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 50,
              background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
              boxShadow: 'var(--shadow-md)', padding: 8, minWidth: 220, marginTop: 4
            }}>
              <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-light)', marginBottom: 4 }}>
                <button style={{ fontSize: 12, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
                  onClick={() => set('filterStatuses', [])}>Clear all</button>
              </div>
              {STATUSES.map(s => (
                <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer', borderRadius: 4, fontSize: 13 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <input type="checkbox" checked={filterStatuses.includes(s.value)} onChange={() => toggleStatus(s.value)} />
                  <span style={{ fontSize: 14 }}>{s.icon}</span>
                  <span style={{ color: s.color, fontWeight: 500 }}>{s.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <select className="filter-select" value={filterArea} onChange={e => set('filterArea', e.target.value)} style={onStyle(!!filterArea)}>
          <option value="">All Areas</option>
          {SERVICE_AREAS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        <select className="filter-select" value={dueFilter} onChange={e => set('dueFilter', e.target.value)} style={onStyle(!!dueFilter)}>
          <option value="">Due: Any</option>
          <option value="overdue">Due: Overdue</option>
          <option value="today">Due: Today</option>
          <option value="week">Due: Next 7 days</option>
          <option value="none">Due: No date</option>
        </select>

        <select className="filter-select" value={filterAssigner} onChange={e => set('filterAssigner', e.target.value)} style={onStyle(!!filterAssigner)}>
          <option value="">Assigned By: All</option>
          <option value={currentUser.id}>Assigned By: Me</option>
          {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
        </select>
        <select className="filter-select" value={filterCollaborator} onChange={e => set('filterCollaborator', e.target.value)} style={onStyle(!!filterCollaborator)}>
          <option value="">Collaborator: All</option>
          {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
        </select>
        <select className="filter-select" value={sortBy} onChange={e => set('sortBy', e.target.value)}>
          <option value="created">Sort: Newest</option>
          <option value="due_asc">Sort: Due Soonest</option>
          <option value="due_desc">Sort: Due Latest</option>
        </select>
        {!forceGroupBy && (
          <select className="filter-select" value={filters.groupBy} onChange={e => set('groupBy', e.target.value)}>
            <option value="none">Group: None</option>
            <option value="client">Group: Client</option>
            <option value="assignee">Group: Assignee</option>
            <option value="status">Group: Status</option>
            <option value="area">Group: Service Area</option>
          </select>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={showUrgentOnly} onChange={e => set('showUrgentOnly', e.target.checked)} />
          <span style={{ color: showUrgentOnly ? 'var(--red)' : 'inherit', fontWeight: showUrgentOnly ? 700 : 400 }}>🔴 Urgent only</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={hideCompleted} onChange={e => set('hideCompleted', e.target.checked)} />
          <span style={{ fontWeight: hideCompleted ? 700 : 400 }}>Hide completed</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={filterOnboarding} onChange={e => set('filterOnboarding', e.target.checked)} />
          <span style={{ fontWeight: filterOnboarding ? 700 : 400 }}>Onboarding only</span>
        </label>
        <div style={{ marginLeft: 'auto' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} task{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Active filter summary — always visible so filters can never be "on" invisibly */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        margin: '10px 0 16px', padding: '8px 12px', borderRadius: 8,
        background: activeCount > 0 ? '#eef2fa' : 'transparent',
        border: activeCount > 0 ? '1px solid #c9d5ec' : '1px dashed var(--border)'
      }}>
        {activeCount === 0 ? (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>No filters active — showing every task.</span>
        ) : (
          <>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy)' }}>
              {activeCount} filter{activeCount !== 1 ? 's' : ''} on:
            </span>
            {chips.map(c => (
              <button key={c.key} onClick={c.clear} title="Remove this filter"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12,
                  background: '#fff', border: '1px solid #c9d5ec', color: 'var(--navy)',
                  borderRadius: 50, padding: '3px 8px', cursor: 'pointer', fontWeight: 500
                }}>
                {c.label} <X size={11} />
              </button>
            ))}
            <button className="btn btn-secondary" onClick={resetFilters}
              style={{ marginLeft: 'auto', fontSize: 12, padding: '5px 12px', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <RotateCcw size={12} /> Reset all filters
            </button>
          </>
        )}
      </div>

      {activeCount > 0 && (
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: -8, marginBottom: 14 }}>
          Your filters stay put while you work and after a refresh. They clear automatically each morning at 9:00 AM.
        </div>
      )}

      {showStatusDropdown && <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setShowStatusDropdown(false)} />}

      {Object.entries(grouped).filter(([, rows]) => rows.length > 0).map(([group, rows]) => (
        <div key={group} className="card" style={{ marginBottom: 16, overflow: 'hidden' }}>
          {groupBy !== 'none' && (
            <div className="card-header" style={{ background: 'var(--cream)', padding: '10px 14px' }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{group}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rows.length}</span>
            </div>
          )}
          <TableContent rows={rows} />
        </div>
      ))}

      {Object.values(grouped).every(rows => rows.length === 0) && (
        <div className="empty-state">
          <CheckSquare size={48} className="empty-state-icon" />
          <h3>No tasks found</h3>
          <p>{activeCount > 0 ? 'Your filters are hiding everything.' : 'Nothing here yet.'}</p>
          {activeCount > 0 && (
            <button className="btn btn-primary" onClick={resetFilters} style={{ marginTop: 12 }}>
              <RotateCcw size={13} /> Reset all filters
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function ApprovalsView({ tasks, profiles, clients, currentUser, onTaskClick, onDeleteTask }) {
  const [showMine, setShowMine] = useState(true)

  const pendingApproval = tasks.filter(t =>
    ['ready_for_review', 'awaiting_client', 'goes_live', 'client_approved'].includes(t.status)
  )

  const myQueue = pendingApproval.filter(t => t.assigned_by === currentUser.id)
  const allQueue = pendingApproval

  const displayed = showMine ? myQueue : allQueue

  const TableContent = ({ rows }) => (
    <table className="task-table" style={{ width: '100%' }}>
      <thead>
        <tr><th>Task</th><th>Client</th><th>Area</th><th>Assigned To</th><th>Due</th><th>Status</th><th></th></tr>
      </thead>
      <tbody>
        {rows.length === 0
          ? <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>
              {showMine ? 'Nothing needs your approval right now 🎉' : 'No tasks pending approval'}
            </td></tr>
          : rows.map(t => <TaskRow key={t.id} task={t} profiles={profiles} clients={clients} onClick={onTaskClick} onDelete={onDeleteTask} />)
        }
      </tbody>
    </table>
  )

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
        <div className="tabs">
          <button className={`tab ${showMine ? 'active' : ''}`} onClick={() => setShowMine(true)}>
            My Queue {myQueue.length > 0 && <span style={{ marginLeft: 5, background: 'var(--gold)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>{myQueue.length}</span>}
          </button>
          <button className={`tab ${!showMine ? 'active' : ''}`} onClick={() => setShowMine(false)}>
            All Pending {allQueue.length > 0 && <span style={{ marginLeft: 5, background: 'var(--text-muted)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>{allQueue.length}</span>}
          </button>
        </div>
      </div>

      {['ready_for_review', 'internally_approved', 'awaiting_client', 'client_approved', 'goes_live'].map(statusVal => {
        const st = getStatus(statusVal)
        const rows = displayed.filter(t => t.status === statusVal)
        if (rows.length === 0) return null
        return (
          <div key={statusVal} className="card" style={{ marginBottom: 16, overflow: 'hidden' }}>
            <div className="card-header" style={{ background: st?.bg, padding: '10px 14px' }}>
              <span style={{ fontWeight: 600, fontSize: 13.5, color: st?.color }}>
                {st?.icon} {st?.label}
              </span>
              <span style={{ fontSize: 12, color: st?.color }}>{rows.length}</span>
            </div>
            <TableContent rows={rows} />
          </div>
        )
      })}

      {displayed.length === 0 && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <TableContent rows={[]} />
        </div>
      )}
    </div>
  )
}

function DashboardView({ tasks, clients, profiles, currentUser, onOpenTasks, onTaskClick }) {
  const me = currentUser.id
  const open = t => !DONE_STATUSES.includes(t.status)
  const mine = tasks.filter(t => t.assigned_to === me && open(t))
  const byMe = tasks.filter(t => t.assigned_by === me && t.assigned_to !== me && open(t))

  const myOverdue = mine.filter(t => isBeforeToday(t.date_due))
  const myToday = mine.filter(t => isDueToday(t.date_due))
  const myWeek = mine.filter(t => isDueWithinDays(t.date_due, 7))
  const myInProgress = mine.filter(t => t.status === 'in_progress')
  const myNotStarted = mine.filter(t => t.status === 'not_started')
  const myBlocked = mine.filter(t => t.status === 'blocked')
  const myUrgent = mine.filter(t => t.is_urgent)
  const myRevisions = mine.filter(t => t.status === 'needs_revision')
  const myReadyToDeploy = mine.filter(t => t.status === 'goes_live')

  const needsMyApproval = tasks.filter(t =>
    t.assigned_by === me && ['ready_for_review', 'awaiting_client', 'goes_live', 'client_approved'].includes(t.status)
  )
  const delegatedOverdue = byMe.filter(t => isBeforeToday(t.date_due))
  const delegatedRevisions = byMe.filter(t => t.status === 'needs_revision')

  // Every number below is a link into All Tasks with the matching filter preset.
  const go = (filters) => onOpenTasks(filters)

  const HERO = [
    {
      label: 'Overdue', sub: 'Assigned to me, past due', value: myOverdue.length, color: 'var(--red)',
      filters: { filterAssignee: me, dueFilter: 'overdue', hideCompleted: true }
    },
    {
      label: 'Due Today', sub: 'Assigned to me', value: myToday.length, color: 'var(--gold)',
      filters: { filterAssignee: me, dueFilter: 'today', hideCompleted: true }
    },
    {
      label: 'Needs My Approval', sub: 'Waiting on you to review', value: needsMyApproval.length, color: 'var(--mauve)',
      filters: { filterAssigner: me, filterStatuses: ['ready_for_review', 'awaiting_client', 'goes_live', 'client_approved'] }
    },
    {
      label: 'My Open Tasks', sub: 'Everything on my plate', value: mine.length, color: 'var(--navy-mid)',
      filters: { filterAssignee: me, hideCompleted: true }
    },
  ]

  const MY_BREAKDOWN = [
    { label: 'In Progress', value: myInProgress.length, color: '#2e7d6e', filters: { filterAssignee: me, filterStatuses: ['in_progress'] } },
    { label: 'Not Started', value: myNotStarted.length, color: '#94a3b8', filters: { filterAssignee: me, filterStatuses: ['not_started'] } },
    { label: 'Blocked', value: myBlocked.length, color: '#c94f4f', filters: { filterAssignee: me, filterStatuses: ['blocked'] } },
    { label: 'Needs Revision', value: myRevisions.length, color: '#c4962a', filters: { filterAssignee: me, filterStatuses: ['needs_revision'] } },
    { label: 'Ready to Deploy', value: myReadyToDeploy.length, color: '#8c4d78', filters: { filterAssignee: me, filterStatuses: ['goes_live'] } },
    { label: 'Urgent', value: myUrgent.length, color: 'var(--red)', filters: { filterAssignee: me, showUrgentOnly: true, hideCompleted: true } },
    { label: 'Due This Week', value: myWeek.length, color: 'var(--navy-mid)', filters: { filterAssignee: me, dueFilter: 'week', hideCompleted: true } },
  ]

  const DELEGATED = [
    { label: 'Overdue', sub: 'Past due with someone else', value: delegatedOverdue.length, color: 'var(--red)', filters: { filterAssigner: me, dueFilter: 'overdue', hideCompleted: true } },
    { label: 'In Revision', sub: 'Sent back, being reworked', value: delegatedRevisions.length, color: '#c4962a', filters: { filterAssigner: me, filterStatuses: ['needs_revision'] } },
    { label: 'All Open', sub: 'Everything I handed off', value: byMe.length, color: 'var(--teal)', filters: { filterAssigner: me, hideCompleted: true } },
  ]

  const StatButton = ({ s, big }) => (
    <button
      onClick={() => go(s.filters)}
      disabled={s.value === 0}
      title={s.value === 0 ? 'Nothing here right now' : `View these ${s.value} in All Tasks`}
      className="card"
      style={{
        textAlign: 'left', cursor: s.value === 0 ? 'default' : 'pointer',
        border: '1px solid var(--border)', borderLeft: `4px solid ${s.value === 0 ? 'var(--border)' : s.color}`,
        background: '#fff', padding: big ? '16px 18px' : '12px 14px',
        opacity: s.value === 0 ? 0.55 : 1, width: '100%',
        display: 'flex', flexDirection: 'column', gap: 2, transition: 'transform .08s ease, box-shadow .08s ease'
      }}
      onMouseEnter={e => { if (s.value > 0) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)' } }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '' }}
    >
      <div style={{ fontFamily: 'var(--font-display)', fontSize: big ? 34 : 24, lineHeight: 1.1, color: s.value === 0 ? 'var(--text-muted)' : s.color }}>{s.value}</div>
      <div style={{ fontSize: big ? 13.5 : 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{s.label}</div>
      {s.sub && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.sub}</div>}
      {s.value > 0 && (
        <div style={{ fontSize: 11, color: s.color, fontWeight: 600, marginTop: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          View in All Tasks →
        </div>
      )}
    </button>
  )

  const SectionTitle = ({ children, note }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '26px 0 10px' }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--navy)', margin: 0 }}>{children}</h3>
      {note && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{note}</span>}
    </div>
  )

  const nothingUrgent = myOverdue.length === 0 && myToday.length === 0 && needsMyApproval.length === 0 && delegatedOverdue.length === 0

  return (
    <div>
      {/* Primary call to action — the dashboard is a summary, the work lives in All Tasks */}
      <div className="card" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        flexWrap: 'wrap', padding: '16px 18px', marginBottom: 18,
        background: 'linear-gradient(90deg, #eef2fa 0%, #fff 60%)', border: '1px solid #c9d5ec'
      }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: 'var(--navy)' }}>
            This is a summary — not your task list.
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3 }}>
            Every number below is a button. Click one to open All Tasks already filtered, or open the full list.
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => go({ filterAssignee: me, hideCompleted: true })}
          style={{ fontSize: 14, padding: '11px 20px', display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <CheckSquare size={16} /> Go to All Tasks
        </button>
      </div>

      {nothingUrgent && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 6, background: '#e8f5f2', border: '1px solid #bfe0d8' }}>
          <span style={{ fontSize: 13.5, color: '#1f5c50', fontWeight: 600 }}>
            🎉 Nothing overdue, nothing due today, nothing waiting on your approval.
          </span>
        </div>
      )}

      <SectionTitle note="what needs you first">Today</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        {HERO.map(s => <StatButton key={s.label} s={s} big />)}
      </div>

      <SectionTitle note="assigned to me">My Work, by status</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        {MY_BREAKDOWN.map(s => <StatButton key={s.label} s={s} />)}
      </div>

      <SectionTitle note="so handed-off work doesn't get forgotten">Assigned by me to others</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {DELEGATED.map(s => <StatButton key={s.label} s={s} />)}
      </div>

      {/* Small, explicitly-labelled peek — capped at 5 so nobody mistakes it for the full list */}
      {myOverdue.length + myToday.length > 0 && (
        <>
          <SectionTitle note="a peek — not the full list">Next up</SectionTitle>
          <div className="card" style={{ overflow: 'hidden' }}>
            <table className="task-table">
              <thead><tr><th>Task</th><th>Client</th><th>Area</th><th>Assigned To</th><th>Due</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {[...myOverdue, ...myToday].slice(0, 5).map(t => (
                  <TaskRow key={t.id} task={t} profiles={profiles} clients={clients} onClick={onTaskClick} onDelete={() => {}} />
                ))}
              </tbody>
            </table>
            <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border-light)', textAlign: 'center' }}>
              <button className="btn btn-secondary" onClick={() => go({ filterAssignee: me, hideCompleted: true })} style={{ fontSize: 12.5 }}>
                See all {mine.length} of my open tasks →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function OnboardingView({ clients, tasks, onClientClick, onRunKickoff }) {
  const onboardingClients = clients.filter(c => c.is_onboarding && !c.onboarding_complete && !c.archived_at)
  return (
    <div>
      <OnboardingSpotlight clients={clients} tasks={tasks} onClientClick={onClientClick} onRunKickoff={onRunKickoff} />
      {onboardingClients.length === 0 && (
        <div className="empty-state">
          <Star size={48} className="empty-state-icon" />
          <h3>No active onboarding clients</h3>
          <p>New clients will appear here when added with the onboarding flag</p>
        </div>
      )}
    </div>
  )
}

function ClientsView({ clients, allClients, tasks, onAddClient, onClientClick, onViewProfile, showArchived, setShowArchived }) {
  const archivedCount = allClients.filter(c => c.archived_at).length
  return (
    <div>
      {/* Filter toggle bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          <Archive size={13} style={{ color: 'var(--text-muted)' }} />
          Show archived clients
          {archivedCount > 0 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>({archivedCount})</span>}
        </label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {clients.map(client => {
          const ct = tasks.filter(t => t.client_id === client.id)
          const done = ct.filter(t => DONE_STATUSES.includes(t.status)).length
          const inProg = ct.filter(t => t.status === 'in_progress').length
          const isArchived = !!client.archived_at
          return (
            <div key={client.id} className="card" style={{ cursor: 'pointer', opacity: isArchived ? 0.7 : 1, borderLeft: isArchived ? '4px solid var(--text-muted)' : undefined }} onClick={() => onClientClick(client.id)}>
              <div className="card-header">
                <div>
                  <span className="card-title">{client.name}</span>
                  {isArchived && (
                    <span style={{ marginLeft: 8, fontSize: 11, background: 'var(--text-muted)', color: '#fff', padding: '2px 8px', borderRadius: 20, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Archive size={10} /> Archived
                    </span>
                  )}
                  {client.is_onboarding && !client.onboarding_complete && !isArchived && (
                    <span style={{ marginLeft: 8, fontSize: 11, background: 'var(--gold)', color: '#fff', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Onboarding</span>
                  )}
                </div>
                <button className="btn-icon" onClick={e => { e.stopPropagation(); onViewProfile && onViewProfile(client) }} title="View profile"><UserCheck size={13} /></button>
              </div>
              <div className="card-body" style={{ paddingTop: 12 }}>
                <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                  <div><span style={{ fontSize: 20, fontFamily: 'var(--font-display)', color: 'var(--navy)' }}>{ct.length}</span><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total</div></div>
                  <div><span style={{ fontSize: 20, fontFamily: 'var(--font-display)', color: 'var(--teal)' }}>{done}</span><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Done</div></div>
                  <div><span style={{ fontSize: 20, fontFamily: 'var(--font-display)', color: 'var(--gold)' }}>{inProg}</span><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>In Progress</div></div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {SERVICE_AREAS.filter(sa => ct.some(t => t.service_area === sa.value)).map(sa => (
                    <span key={sa.value} className="badge" style={{ background: sa.color + '18', color: sa.color, fontSize: 11 }}>
                      <span className="service-dot" style={{ background: sa.color }} />{sa.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )
        })}
        <div className="card" style={{ cursor: 'pointer', border: '2px dashed var(--border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120, boxShadow: 'none' }}
          onClick={onAddClient}>
          <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <Plus size={24} style={{ margin: '0 auto 6px' }} />
            <div style={{ fontSize: 13 }}>Add Client</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function TeamView({ profiles, tasks }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
      {profiles.filter(isActiveProfile).map(p => {
        const pt = tasks.filter(t => t.assigned_to === p.id)
        const colors = ['#2e3a59', '#2e7d6e', '#c4962a', '#9c5fa3', '#e8724a', '#3d7fc4']
        const color = colors[profiles.indexOf(p) % colors.length]
        return (
          <div key={p.id} className="card card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div className="avatar" style={{ width: 40, height: 40, fontSize: 15, background: color }}>
                {p.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{p.full_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{ROLES.find(r => r.value === p.role)?.label || p.role}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div><span style={{ fontSize: 22, fontFamily: 'var(--font-display)', color: 'var(--navy)' }}>{pt.filter(t => !DONE_STATUSES.includes(t.status)).length}</span><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Open</div></div>
              <div><span style={{ fontSize: 22, fontFamily: 'var(--font-display)', color: 'var(--teal)' }}>{pt.filter(t => DONE_STATUSES.includes(t.status)).length}</span><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Done</div></div>
              <div><span style={{ fontSize: 22, fontFamily: 'var(--font-display)', color: 'var(--red)' }}>{pt.filter(t => t.status === 'blocked').length}</span><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Blocked</div></div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Onboarding intake helpers ────────────────────────────────────────────────

// Ordinal: 1 -> "1st", 2 -> "2nd", etc.
function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// Billing day comes from the client's start_date (a YYYY-MM-DD date string).
// Per the rule: day 28 or later => "the last day of each month" (since not every
// month has a 28th-31st). Days 1-27 => "the Nth of each month".
function billingDayPhrase(startDateStr) {
  if (!startDateStr) return 'the [BILLING DAY] of each month'
  // Parse at noon to avoid timezone shifting the day backward.
  const d = new Date(startDateStr + 'T12:00:00')
  if (isNaN(d.getTime())) return 'the [BILLING DAY] of each month'
  const day = d.getDate()
  if (day >= 28) return 'the last day of each month'
  return `the ${ordinal(day)} of each month`
}

function firstNameFrom(fullName) {
  if (!fullName || !fullName.trim()) return 'there'
  return fullName.trim().split(/\s+/)[0]
}

// Kickoff meeting datetime -> "May 21st at 10:00 AM Pacific"
function meetingPhrase(meetingAtStr) {
  if (!meetingAtStr) return '[KICKOFF MEETING DATE & TIME]'
  const d = new Date(meetingAtStr)
  if (isNaN(d.getTime())) return '[KICKOFF MEETING DATE & TIME]'
  const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${date} at ${time} Pacific`
}

// Mirrors the real welcome email. Only the name and bill date are dynamic;
// the Info Grab link fills in if saved, otherwise a clear placeholder. The
// Intuit link is always a placeholder (it's generated per-client in QuickBooks).
function buildWelcomeEmailBody({ contactName, meetingAt, startDate, onboardingUrl }) {
  const first = firstNameFrom(contactName)
  const meeting = meetingPhrase(meetingAt)
  const billing = billingDayPhrase(startDate)
  const grab = onboardingUrl || '[ONBOARDING LINK]'
  return `Hi ${first},

Welcome again to LMR!

We are looking forward to the Kick Off Meeting on ${meeting}. Please let me know if you did not get the meeting invite.

We will need the following items before the Kick Off Meeting.

(1) Please fill this out for recurring payments — payments will be charged on ${billing}:
[PASTE INTUIT PAYMENT LINK]

(2) Here is your personal onboarding page:
${grab}

It walks you through a few questions and helps you give us access to your accounts, step by step. Your answers save as you go, so you can stop and come back anytime. If you get stuck on anything, just choose "I need help" and we will take it from there.

As you are going through your onboarding docs, please do not hesitate to reach out with any questions that come up.`
}

function gmailComposeUrl({ to, subject, body }) {
  const p = new URLSearchParams()
  if (to) p.set('to', to)
  if (subject) p.set('su', subject)
  if (body) p.set('body', body)
  return `https://mail.google.com/mail/?view=cm&fs=1&${p.toString()}`
}

function IntakeHeader({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', margin: '18px 0 8px', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      {children}
    </div>
  )
}

function AddClientModal({ onClose, onSaved, toast, currentUser, profiles = [] }) {
  // Basics
  const [name, setName] = useState('')
  // Team assignment — set at intake so the AD scorecard can attribute new clients
  const [accountDirectorId, setAccountDirectorId] = useState('')
  const [accountManagerId, setAccountManagerId] = useState('')
  const [isOnboarding, setIsOnboarding] = useState(true)
  const [googleDocId, setGoogleDocId] = useState('')
  const [saving, setSaving] = useState(false)

  // Intake (all optional)
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [startDate, setStartDate] = useState('')          // -> clients.start_date (also the billing day)
  const [meetingAt, setMeetingAt] = useState('')          // -> clients.kickoff_meeting_at
  const [contractLink, setContractLink] = useState('')    // -> client_documents row
  // Services -> clients.has_* flags. Drives which sections + access requests the
  // client's onboarding page shows (replaces the 5 info-grab sheet templates).
  const [services, setServices] = useState({})
  const toggleService = (flag) => setServices(prev => ({ ...prev, [flag]: !prev[flag] }))
  const [onboardingUrl, setOnboardingUrl] = useState('')  // generated on save

  // Onboarding tracking (checked at add-time, saved on the client record)
  // Commission: only shown to people who can see commission (Lauren + Tori).
  const showCommission = canViewCommission(currentUser?.id)
  const [earnsCommission, setEarnsCommission] = useState(false)
  const [monthlyRetainer, setMonthlyRetainer] = useState('')   // full amount the client pays
  const commissionIncomplete = isOnboarding && earnsCommission && (!startDate || monthlyRetainer === '')
  const [recurringPaymentStarted, setRecurringPaymentStarted] = useState(false)

  // Plan fields (reuse existing clients columns)
  const [planWebsite, setPlanWebsite] = useState('')
  const [planAds, setPlanAds] = useState('')
  const [planSocial, setPlanSocial] = useState('')
  const [planNewsletters, setPlanNewsletters] = useState('')
  const [planSeoBlogs, setPlanSeoBlogs] = useState('')
  const [planMeetings, setPlanMeetings] = useState('')

  // The just-saved client (enables the welcome-email + done buttons after save)
  const [savedClient, setSavedClient] = useState(null)

  const save = async () => {
    if (!name.trim()) return
    setSaving(true)

    const clientRow = {
      name: name.trim(),
      is_onboarding: isOnboarding,
      google_doc_id: googleDocId.trim() || null,
      account_director_id: accountDirectorId || null,
      account_manager_id: accountManagerId || null,
    }
    if (isOnboarding) {
      if (contactName.trim()) clientRow.primary_contact_name = contactName.trim()
      if (contactEmail.trim()) clientRow.contact_email = contactEmail.trim()
      if (startDate) clientRow.start_date = startDate
      if (meetingAt) clientRow.kickoff_meeting_at = meetingAt
      SERVICES.forEach(sv => { clientRow[sv.flag] = !!services[sv.flag] })
      if (services.has_website && planWebsite.trim()) clientRow.plan_website = planWebsite.trim()
      if (services.has_ads && planAds.trim()) clientRow.plan_ads = planAds.trim()
      if (services.has_social && planSocial.trim()) clientRow.plan_social = planSocial.trim()
      if (services.has_newsletters && planNewsletters.trim()) clientRow.plan_newsletters = planNewsletters.trim()
      if ((services.has_seo || services.has_blogs) && planSeoBlogs.trim()) clientRow.plan_seo_blogs = planSeoBlogs.trim()
      if (planMeetings.trim()) clientRow.plan_meetings = planMeetings.trim()
      clientRow.commission_recorded = showCommission && earnsCommission
      clientRow.recurring_payment_started = recurringPaymentStarted
    }

    const { data, error } = await supabase.from('clients')
      .insert(clientRow)
      .select().single()
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }

    // Save links into the Documents tab (client_documents), best-effort.
    if (isOnboarding && data) {
      const docRows = []

      // Generate the client's secret onboarding link + the access items their services need.
      const token = generateOnboardingToken()
      const { error: linkErr } = await supabase.from('onboarding_links')
        .insert({ client_id: data.id, token, created_by: currentUser?.id || null })
      if (linkErr) {
        toast('Client saved, but the onboarding link failed: ' + linkErr.message, 'error')
      } else {
        const url = onboardingUrlFor(token)
        setOnboardingUrl(url)
        await supabase.from('clients').update({ info_grab_link: url }).eq('id', data.id)
        docRows.push({ client_id: data.id, label: 'Onboarding Info Grab', url, notes: null, created_by: currentUser?.id })

        const accessRows = platformsForServices(services).map(platform => ({ client_id: data.id, platform }))
        if (accessRows.length > 0) {
          const { error: accErr } = await supabase.from('client_access_items').insert(accessRows)
          if (accErr) toast('Client saved, but access items failed: ' + accErr.message, 'error')
        }
      }
      if (contractLink.trim()) {
        docRows.push({ client_id: data.id, label: 'Signed Contract / Proposal', url: contractLink.trim(), notes: null, created_by: currentUser?.id })
      }
      if (docRows.length > 0) {
        const { error: docErr } = await supabase.from('client_documents').insert(docRows)
        if (docErr) toast('Client saved, but a document link failed: ' + docErr.message, 'error')
      }
    }

    if (isOnboarding && data && showCommission && earnsCommission) {
      const { error: comErr } = await supabase.from('commission_deals').insert({
        client_id: data.id,
        client_name: data.name,
        start_date: startDate,
        monthly_retainer: Number(monthlyRetainer),
        created_by: currentUser?.id || null,
      })
      if (comErr) toast('Client saved, but commission failed: ' + comErr.message, 'error')
    }

    toast('Client added', 'success')
    setSaving(false)
    setSavedClient(data)   // reveal welcome-email + finish buttons; do NOT auto-launch Kickoff Builder
  }

  // ── After-save view: welcome email + finish ────────────────────────────────
  if (savedClient) {
    const body = buildWelcomeEmailBody({ contactName, meetingAt, startDate, onboardingUrl })
    const subject = `Welcome to LMR — ${savedClient.name}`
    const composeHref = gmailComposeUrl({ to: contactEmail.trim(), subject, body })

    const copyEmail = async () => {
      try {
        await navigator.clipboard.writeText(body)
        toast('Email text copied', 'success')
      } catch {
        toast('Could not copy — select the text manually', 'error')
      }
    }

    return (
      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && (onSaved && onSaved())}>
        <div className="modal" style={{ maxWidth: 560 }}>
          <div className="modal-header">
            <span className="modal-title">{savedClient.name} added</span>
            <button className="btn-icon" onClick={() => onSaved && onSaved()}><X size={16} /></button>
          </div>
          <div className="modal-body">
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
              They're in the New Clients tab now. Run the Kickoff Builder there after the kickoff meeting.
            </p>

            <IntakeHeader>Welcome email</IntakeHeader>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 0 }}>
              Opens a Gmail draft pre-filled with the name{startDate ? ', billing day' : ''}{meetingAt ? ', meeting time' : ''}
              {onboardingUrl ? ', and their onboarding link' : ''}. You'll still paste the Intuit payment link
              {onboardingUrl ? '' : ' and the onboarding link'} (marked in the draft).
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a className="btn btn-gold" href={composeHref} target="_blank" rel="noreferrer">Open Gmail draft</a>
              <button className="btn btn-ghost" onClick={copyEmail}>Copy email text</button>
            </div>
            <div style={{ marginTop: 12, padding: 12, background: 'var(--cream)', borderRadius: 8, whiteSpace: 'pre-wrap', fontSize: 12.5, color: 'var(--text-secondary)', maxHeight: 200, overflow: 'auto', border: '1px solid var(--border)' }}>
              {body}
            </div>

            <IntakeHeader>Still to do (manual)</IntakeHeader>
            <ul style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0, paddingLeft: 18 }}>
              <li>Mark won in HubSpot</li>
              <li>Create the recurring payment in QuickBooks, then paste its link into the email</li>
            </ul>
          </div>
          <div className="modal-footer">
            <button className="btn btn-gold" onClick={() => onSaved && onSaved()}>Done</button>
          </div>
        </div>
      </div>
    )
  }

  // ── Intake form ─────────────────────────────────────────────────────────────
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">Add Client</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="form-group">
            <label className="form-label">Client Name</label>
            <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Healing the Human Experience" autoFocus />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={isOnboarding} onChange={e => setIsOnboarding(e.target.checked)} />
            New client (show in New Clients tab + fill out onboarding below)
          </label>

          {/* Team — drives the AD scorecard's "new clients" count for this month */}
          <div className="form-row" style={{ marginTop: 14 }}>
            <div className="form-group">
              <label className="form-label">Account Director</label>
              <select className="form-control" value={accountDirectorId} onChange={e => setAccountDirectorId(e.target.value)}>
                <option value="">— Select —</option>
                {profiles
                  .filter(p => p.role === 'ad' || p.role === 'supervising_ad')
                  .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
                  .map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Account Manager <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
              <select className="form-control" value={accountManagerId} onChange={e => setAccountManagerId(e.target.value)}>
                <option value="">— None —</option>
                {profiles
                  .filter(p => p.role === 'am' || p.role === 'ad' || p.role === 'supervising_ad')
                  .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
                  .map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
          </div>

          {!isOnboarding && (
            <div className="form-group" style={{ marginTop: 14 }}>
              <label className="form-label">Meeting Notes Google Doc ID <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
              <input className="form-control" value={googleDocId} onChange={e => setGoogleDocId(e.target.value)} placeholder="docs.google.com/document/d/THIS_PART/edit" />
            </div>
          )}

          {isOnboarding && (
            <>
              <IntakeHeader>Contact</IntakeHeader>
              <div className="form-group">
                <label className="form-label">Primary contact name</label>
                <input className="form-control" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="e.g. Jane Doe" />
              </div>
              <div className="form-group">
                <label className="form-label">Contact email <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(welcome email goes here)</span></label>
                <input className="form-control" type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="jane@example.com" />
              </div>

              <IntakeHeader>Dates</IntakeHeader>
              <div className="form-group">
                <label className="form-label">Start date <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(also the monthly billing day)</span></label>
                <input className="form-control" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                {startDate && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Email will say: charged on {billingDayPhrase(startDate)}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Kick Off Meeting <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(date &amp; time)</span></label>
                <input className="form-control" type="datetime-local" value={meetingAt} onChange={e => setMeetingAt(e.target.value)} />
              </div>

              <IntakeHeader>Services</IntakeHeader>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 0 }}>
                Check what they signed up for. This builds their onboarding page, so they only get asked about accounts we actually need.
              </p>
              {SERVICES.map(sv => {
                const plan = {
                  plan_website: [planWebsite, setPlanWebsite],
                  plan_ads: [planAds, setPlanAds],
                  plan_social: [planSocial, setPlanSocial],
                  plan_newsletters: [planNewsletters, setPlanNewsletters],
                  plan_seo_blogs: [planSeoBlogs, setPlanSeoBlogs],
                }[sv.planField]
                // SEO and Blogs share one plan field; show it once, under whichever is checked first.
                const sharedAlreadyShown = sv.flag === 'has_blogs' && services.has_seo
                return (
                  <div key={sv.flag} style={{ marginBottom: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={!!services[sv.flag]} onChange={() => toggleService(sv.flag)} />
                      {sv.icon} {sv.label}
                    </label>
                    {services[sv.flag] && plan && !sharedAlreadyShown && (
                      <input className="form-control" style={{ marginTop: 6 }} value={plan[0]} onChange={e => plan[1](e.target.value)} placeholder={sv.placeholder} />
                    )}
                  </div>
                )
              })}
              <div className="form-group" style={{ marginTop: 10 }}>
                <label className="form-label">Meetings</label>
                <input className="form-control" value={planMeetings} onChange={e => setPlanMeetings(e.target.value)} placeholder='e.g. "Bi-weekly check-ins"' />
              </div>

              <IntakeHeader>Contract</IntakeHeader>
              <div className="form-group">
                <label className="form-label">Signed contract / proposal link <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(saves to Documents tab)</span></label>
                <input className="form-control" value={contractLink} onChange={e => setContractLink(e.target.value)} placeholder="https://..." />
              </div>

              <IntakeHeader>Onboarding steps</IntakeHeader>
              {showCommission && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={earnsCommission} onChange={e => setEarnsCommission(e.target.checked)} />
                    Tori brought in / closed this client (earns commission)
                  </label>
                  {earnsCommission && (
                    <div className="form-group" style={{ marginTop: 8, marginLeft: 24 }}>
                      <label className="form-label">Monthly retainer <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(the full amount the client pays)</span></label>
                      <input className="form-control" type="number" step="0.01" min="0" value={monthlyRetainer}
                        onChange={e => setMonthlyRetainer(e.target.value)} placeholder="e.g. 1600" style={{ maxWidth: 180 }} />
                      <div style={{ fontSize: 11.5, color: commissionIncomplete ? 'var(--red)' : 'var(--text-muted)', marginTop: 5 }}>
                        {!startDate
                          ? 'Add the start date above. Commission starts that month.'
                          : monthlyRetainer === ''
                            ? 'Enter the retainer to record commission.'
                            : (() => {
                                const w = dealWindow({ start_date: startDate })
                                return `${fmtMoney(commissionFor(monthlyRetainer))}/mo for Tori, ${monthLabel(w[0])} – ${monthLabel(w[w.length - 1])}`
                              })()}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={recurringPaymentStarted} onChange={e => setRecurringPaymentStarted(e.target.checked)} />
                Start recurring payment
              </label>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving || !name.trim() || commissionIncomplete}>{saving ? 'Adding...' : 'Add Client'}</button>
        </div>
      </div>
    </div>
  )
}

function DeleteConfirmModal({ task, onClose, onConfirm }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <span className="modal-title">Delete Task</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
            Are you sure you want to delete <strong>"{task.title}"</strong>? This cannot be undone.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>Delete Task</button>
        </div>
      </div>
    </div>
  )
}

function SetupProfileModal({ user, onSaved, toast }) {
  const [name, setName] = useState(user.user_metadata?.full_name || '')
  const [role, setRole] = useState('am')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    const { error } = await supabase.from('profiles').upsert({ id: user.id, full_name: name.trim(), role })
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    onSaved()
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header"><span className="modal-title">Set Up Your Profile</span></div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Your Name</label>
            <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" />
          </div>
          <div className="form-group">
            <label className="form-label">Your Role</label>
            <select className="form-control" value={role} onChange={e => setRole(e.target.value)}>
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-gold" onClick={save} disabled={saving || !name.trim()}>{saving ? 'Saving...' : 'Save & Continue'}</button>
        </div>
      </div>
    </div>
  )
}

const MESSAGE_TYPES = ['mention', 'comment']
const isMessage = (n) => MESSAGE_TYPES.includes(n.type)

function NotifPanel({ notifications, onMarkRead, onTaskClick, tasks }) {
  const [tab, setTab] = useState('messages') // always default to Messages on open

  const getIcon = (type) => {
    if (MESSAGE_TYPES.includes(type)) return '💬'
    if (type === 'needs_revision') return '↩'
    if (type === 'ready_for_review') return '👁'
    if (type === 'due_today') return '⚠️'
    if (type === 'due_tomorrow') return '📅'
    if (type === 'overdue') return '🔴'
    if (type?.includes('ticket')) return '🎫'
    return '🔔'
  }

  const messages = notifications.filter(isMessage)
  const others = notifications.filter(n => !isMessage(n))
  const msgUnread = messages.some(n => !n.read)
  const otherUnread = others.some(n => !n.read)

  const list = tab === 'messages' ? messages : others
  const currentHasUnread = tab === 'messages' ? msgUnread : otherUnread

  const tabBtn = (key, label, hasUnread) => (
    <button
      onClick={() => setTab(key)}
      style={{
        flex: 1, padding: '8px 4px', fontSize: 12.5, fontWeight: tab === key ? 700 : 500,
        background: 'none', border: 'none', cursor: 'pointer',
        color: tab === key ? 'var(--navy)' : 'var(--text-muted)',
        borderBottom: tab === key ? '2px solid var(--gold)' : '2px solid transparent',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}
    >
      {label}
      {hasUnread && <span style={{ width: 7, height: 7, borderRadius: 50, background: 'var(--mauve)', display: 'inline-block' }} />}
    </button>
  )

  return (
    <div className="notif-panel">
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-light)' }}>
        {tabBtn('messages', 'Messages', msgUnread)}
        {tabBtn('other', 'Everything else', otherUnread)}
      </div>

      <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'flex-end', minHeight: 20 }}>
        {currentHasUnread && (
          <button className="btn btn-ghost btn-sm" onClick={() => onMarkRead(tab)}>Mark all read</button>
        )}
      </div>

      {list.length === 0
        ? <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{tab === 'messages' ? '💬' : '🔔'}</div>
            {tab === 'messages' ? 'No messages yet' : 'Nothing else right now'}
          </div>
        : list.slice(0, 15).map(n => {
            const task = n.task_id && tasks ? tasks.find(t => t.id === n.task_id) : null
            return (
              <div key={n.id} className={`notif-item ${!n.read ? 'unread' : ''}`}
                onClick={() => task && onTaskClick && onTaskClick(task)}
                style={{ cursor: task ? 'pointer' : 'default' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{getIcon(n.type)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="notif-msg" style={{ fontWeight: n.read ? 400 : 600 }}>{n.message}</div>
                    <div className="notif-time">{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</div>
                  </div>
                </div>
              </div>
            )
          })
      }
    </div>
  )
}


function RevisionsView({ tasks, profiles, clients, currentUser, onTaskClick, onDeleteTask }) {
  const [filterMine, setFilterMine] = useState(true)
  const revisionTasks = tasks.filter(t => t.status === 'needs_revision')
  const myRevisions = revisionTasks.filter(t => t.assigned_to === currentUser.id || (t.collaborator_ids || []).includes(currentUser.id))
  const assignedByMeRevisions = revisionTasks.filter(t => t.assigned_by === currentUser.id)
  const displayed = filterMine ? myRevisions : assignedByMeRevisions

  return (
    <div>
      <div style={{ background: '#fff8e1', border: '1.5px solid #f0d98a', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 20 }}>↩</span>
        <span style={{ fontSize: 13.5, color: '#8a6000', fontWeight: 500 }}>
          {revisionTasks.length} task{revisionTasks.length !== 1 ? 's' : ''} currently need revisions
        </span>
      </div>
      <div className="tabs" style={{ marginBottom: 20 }}>
        <button className={`tab ${filterMine ? 'active' : ''}`} onClick={() => setFilterMine(true)}>
          My Revisions {myRevisions.length > 0 && <span style={{ marginLeft: 5, background: '#c4962a', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>{myRevisions.length}</span>}
        </button>
        <button className={`tab ${!filterMine ? 'active' : ''}`} onClick={() => setFilterMine(false)}>
          Awaiting Revision {assignedByMeRevisions.length > 0 && <span style={{ marginLeft: 5, background: 'var(--text-muted)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>{assignedByMeRevisions.length}</span>}
        </button>
      </div>
      {displayed.length === 0 ? (
        <div className="empty-state">
          <GitMerge size={48} className="empty-state-icon" />
          <h3>{filterMine ? 'No revisions assigned to you' : 'No revisions awaiting from your assignees'}</h3>
          <p>Tasks sent back for revision will appear here</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="task-table" style={{ width: '100%' }}>
            <thead><tr><th>Task</th><th>Client</th><th>Area</th><th>{filterMine ? 'Assigned By' : 'Assigned To'}</th><th>Due</th><th>Status</th><th></th></tr></thead>
            <tbody>{displayed.map(t => <TaskRow key={t.id} task={t} profiles={profiles} clients={clients} onClick={onTaskClick} onDelete={onDeleteTask} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function AssignedByMeView({ tasks, profiles, clients, currentUser, onTaskClick, onDeleteTask }) {
  const [groupBy, setGroupBy] = useState('assignee')
  const [hideCompleted, setHideCompleted] = useState(true)
  const myAssigned = tasks.filter(t => t.assigned_by === currentUser.id)
  const filtered = hideCompleted ? myAssigned.filter(t => !DONE_STATUSES.includes(t.status)) : myAssigned
  const grouped = {}
  filtered.forEach(t => {
    let key
    if (groupBy === 'assignee') { const p = profiles.find(p => p.id === t.assigned_to); key = p?.full_name || 'Unassigned' }
    else if (groupBy === 'client') { const c = clients.find(c => c.id === t.client_id); key = c?.name || 'No Client' }
    else { const st = getStatus(t.status); key = st?.label || 'Unknown' }
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(t)
  })
  const needingAction = myAssigned.filter(t => ['ready_for_review','needs_revision','awaiting_client','goes_live','client_approved'].includes(t.status))
  const overdueAssigned = myAssigned.filter(t => isBeforeToday(t.date_due) && !DONE_STATUSES.includes(t.status))

  return (
    <div>
      {overdueAssigned.length > 0 && (
        <div className="card" style={{ overflow: 'hidden', border: '1.5px solid var(--urgent)', marginBottom: 20 }}>
          <div className="card-header" style={{ background: 'var(--red-bg)' }}>
            <span className="card-title" style={{ color: 'var(--urgent)' }}>⏰ Overdue</span>
            <span style={{ fontSize: 12, color: 'var(--urgent)' }}>{overdueAssigned.length}</span>
          </div>
          <table className="task-table">
            <thead><tr><th>Task</th><th>Client</th><th>Area</th><th>Assigned To</th><th>Due</th><th>Status</th><th></th></tr></thead>
            <tbody>{overdueAssigned.map(t => <TaskRow key={t.id} task={t} profiles={profiles} clients={clients} onClick={onTaskClick} onDelete={onDeleteTask} />)}</tbody>
          </table>
        </div>
      )}
      {needingAction.length > 0 && (
        <div className="card" style={{ overflow: 'hidden', border: '1.5px solid var(--mauve)', marginBottom: 20 }}>
          <div className="card-header" style={{ background: 'var(--blush)' }}>
            <span className="card-title" style={{ color: 'var(--mauve)' }}>⭐ Needs Your Attention</span>
            <span style={{ fontSize: 12, color: 'var(--mauve)' }}>{needingAction.length}</span>
          </div>
          <table className="task-table">
            <thead><tr><th>Task</th><th>Client</th><th>Area</th><th>Assigned To</th><th>Due</th><th>Status</th><th></th></tr></thead>
            <tbody>{needingAction.map(t => <TaskRow key={t.id} task={t} profiles={profiles} clients={clients} onClick={onTaskClick} onDelete={onDeleteTask} />)}</tbody>
          </table>
        </div>
      )}
      <div className="filters-bar">
        <select className="filter-select" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
          <option value="assignee">Group by Assignee</option>
          <option value="client">Group by Client</option>
          <option value="status">Group by Status</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={hideCompleted} onChange={e => setHideCompleted(e.target.checked)} />
          Hide completed
        </label>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>{filtered.length} tasks</span>
      </div>
      {Object.entries(grouped).filter(([,rows]) => rows.length > 0).map(([group, rows]) => (
        <div key={group} className="card" style={{ marginBottom: 14, overflow: 'hidden' }}>
          <div className="card-header" style={{ background: 'var(--cream)', padding: '10px 14px' }}>
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>{group}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rows.length}</span>
          </div>
          <table className="task-table">
            <thead><tr><th>Task</th><th>Client</th><th>Area</th><th>Assigned To</th><th>Due</th><th>Status</th><th></th></tr></thead>
            <tbody>{rows.map(t => <TaskRow key={t.id} task={t} profiles={profiles} clients={clients} onClick={onTaskClick} onDelete={onDeleteTask} />)}</tbody>
          </table>
        </div>
      ))}
      {filtered.length === 0 && (
        <div className="empty-state">
          <UserCheck size={48} className="empty-state-icon" />
          <h3>No tasks assigned by you</h3>
          <p>Tasks you create and assign to others will appear here</p>
        </div>
      )}
    </div>
  )
}

function AdminView({ profiles, tasks, currentUser, profile, onReload }) {
  const toast = useToast()
  const [reassignModal, setReassignModal] = useState(null)
  const [editRoleModal, setEditRoleModal] = useState(null)
  const [reassignTo, setReassignTo] = useState('')
  const [newRole, setNewRole] = useState('')
  const [saving, setSaving] = useState(false)
  const canAdmin = profile?.role === 'supervising_ad'

  const getUserTaskCount = (userId) => ({
    assigned: tasks.filter(t => t.assigned_to === userId && !DONE_STATUSES.includes(t.status)).length,
    created: tasks.filter(t => t.assigned_by === userId && !DONE_STATUSES.includes(t.status)).length,
  })

  const reassignTasks = async () => {
    if (!reassignModal || !reassignTo) return
    setSaving(true)
    try {
      // supabase-js returns { error } instead of throwing, so check each result
      const r1 = await supabase.from('tasks').update({ assigned_to: reassignTo }).eq('assigned_to', reassignModal.userId)
      if (r1.error) throw r1.error
      const r2 = await supabase.from('tasks').update({ assigned_by: reassignTo }).eq('assigned_by', reassignModal.userId)
      if (r2.error) throw r2.error
      toast('Tasks reassigned from ' + reassignModal.userName, 'success')
      setReassignModal({ ...reassignModal, done: true })
      setReassignTo('')
      onReload()
    } catch (e) { toast('Error: ' + e.message, 'error') }
    finally { setSaving(false) }
  }

  // Deactivate = can't log in, hidden from pickers, all history kept.
  // Runs through the set_user_active database function (see
  // supabase/migrations/deactivate_users.sql), which checks that the caller
  // is the Supervising AD and also bans the login in Supabase Auth.
  const setActive = async (p, makeActive) => {
    const msg = makeActive
      ? `Reactivate ${p.full_name}? They'll be able to log in again.`
      : `Deactivate ${p.full_name}? They won't be able to log in, and they'll be hidden from assignment lists. All their past tasks, comments, and time entries are kept.`
    if (!window.confirm(msg)) return
    setSaving(true)
    const { error } = await supabase.rpc('set_user_active', { target_user: p.id, make_active: makeActive })
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast(`${p.full_name} ${makeActive ? 'reactivated' : 'deactivated'}`, 'success')
    onReload()
  }

  const saveRole = async () => {
    if (!editRoleModal || !newRole) return
    setSaving(true)
    const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', editRoleModal.userId)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    toast('Role updated', 'success')
    setEditRoleModal(null); setNewRole(''); onReload(); setSaving(false)
  }

  if (!canAdmin) return (
    <div className="empty-state">
      <Shield size={48} className="empty-state-icon" />
      <h3>Admin access required</h3>
      <p>Only the Supervising AD can manage users</p>
    </div>
  )

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--lightblue-bg)', padding: '10px 14px', borderRadius: 10, marginBottom: 20 }}>
        📧 To restrict sign-ups to @marketingbylmr.com: Supabase → Authentication → Sign In / Providers → Email → Restrict signups
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="card-header"><span className="card-title">Team Members</span></div>
        <table className="task-table" style={{ width: '100%' }}>
          <thead><tr><th>Name</th><th>Role</th><th>Status</th><th>Open (assigned to)</th><th>Open (assigned by)</th><th>Actions</th></tr></thead>
          <tbody>
            {[...profiles].sort((a, b) => Number(isActiveProfile(b)) - Number(isActiveProfile(a))).map(p => {
              const counts = getUserTaskCount(p.id)
              const isMe = p.id === currentUser.id
              const active = isActiveProfile(p)
              return (
                <tr key={p.id} style={{ cursor: 'default', opacity: active ? 1 : 0.55 }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="avatar" style={{ background: 'var(--navy)', width: 32, height: 32, fontSize: 12 }}>
                        {p.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>{p.full_name} {isMe && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(you)</span>}</div>
                    </div>
                  </td>
                  <td><span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 50, background: 'var(--cream-dark)', color: 'var(--text-secondary)' }}>{ROLES.find(r => r.value === p.role)?.label || p.role}</span></td>
                  <td>
                    <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 50, fontWeight: 600, background: active ? '#e6f4ea' : 'var(--cream-dark)', color: active ? '#1e7b3c' : 'var(--text-muted)' }}>
                      {active ? 'Active' : 'Deactivated'}
                    </span>
                  </td>
                  <td><span style={{ fontWeight: counts.assigned > 0 ? 600 : 400, color: counts.assigned > 5 ? 'var(--red)' : 'var(--text-primary)' }}>{counts.assigned}</span></td>
                  <td><span style={{ fontWeight: counts.created > 0 ? 600 : 400 }}>{counts.created}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditRoleModal({ userId: p.id, userName: p.full_name, role: p.role }); setNewRole(p.role) }}>Edit Role</button>
                      {!isMe && <button className="btn btn-ghost btn-sm" onClick={() => setReassignModal({ userId: p.id, userName: p.full_name })}>Reassign</button>}
                      {!isMe && (active
                        ? <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} disabled={saving} onClick={() => setActive(p, false)}>Deactivate</button>
                        : <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => setActive(p, true)}>Reactivate</button>)}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {reassignModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setReassignModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <span className="modal-title">{reassignModal.done ? 'Tasks Reassigned ✓' : 'Reassign Tasks from ' + reassignModal.userName}</span>
              <button className="btn-icon" onClick={() => setReassignModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {reassignModal.done ? (
                <div>
                  <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginBottom: 16 }}>
                    ✅ All tasks reassigned. To stop <strong>{reassignModal.userName}</strong> from logging in while keeping their history, click <strong>Deactivate</strong> next to their name.
                  </div>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginBottom: 16 }}>All tasks assigned to or by <strong>{reassignModal.userName}</strong> will be reassigned.</p>
                  <div className="form-group">
                    <label className="form-label">Reassign to</label>
                    <select className="form-control" value={reassignTo} onChange={e => setReassignTo(e.target.value)}>
                      <option value="">Select team member...</option>
                      {profiles.filter(p => isActiveProfile(p) && p.id !== reassignModal.userId).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                    </select>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setReassignModal(null)}>{reassignModal.done ? 'Done' : 'Cancel'}</button>
              {!reassignModal.done && <button className="btn btn-danger" onClick={reassignTasks} disabled={saving || !reassignTo}>{saving ? 'Reassigning...' : 'Reassign All Tasks'}</button>}
            </div>
          </div>
        </div>
      )}

      {editRoleModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditRoleModal(null)}>
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <span className="modal-title">Edit Role — {editRoleModal.userName}</span>
              <button className="btn-icon" onClick={() => setEditRoleModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-control" value={newRole} onChange={e => setNewRole(e.target.value)}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditRoleModal(null)}>Cancel</button>
              <button className="btn btn-gold" onClick={saveRole} disabled={saving}>{saving ? 'Saving...' : 'Save Role'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


export default function App() {
  const toast = useToast()
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tasks, setTasks] = useState([])
  const [allClients, setAllClients] = useState([]) // raw list including archived
  const [profiles, setProfiles] = useState([])
  const [deactivatedNotice, setDeactivatedNotice] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [view, setView] = useState('dashboard')
  const [tasksPreset, setTasksPreset] = useState(null)
  const [filterClientId, setFilterClientId] = useState(null)
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [editTask, setEditTask] = useState(null)
  const [detailTask, setDetailTask] = useState(null)
  const [deleteTask, setDeleteTask] = useState(null)
  const [showAddClient, setShowAddClient] = useState(false)
  const [showNotifs, setShowNotifs] = useState(false)
  const [showProfileSetup, setShowProfileSetup] = useState(false)
  const [allProjects, setAllProjects] = useState([])
  const [selectedProjectId, setSelectedProjectId] = useState(null)
  const [viewingClientProfile, setViewingClientProfile] = useState(null)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [selectedBlogId, setSelectedBlogId] = useState(null)
  const [selectedBlogTaskId, setSelectedBlogTaskId] = useState(null)
  const [selectedBatchId, setSelectedBatchId] = useState(null)
  const [selectedBatchTaskId, setSelectedBatchTaskId] = useState(null)
  const [selectedNote, setSelectedNote] = useState(null)
  // When the task detail panel is opened from inside a view that loads its own
  // tasks (the client profile), keep that view's refresher so edits made in the
  // panel show up in the list behind it.
  const detailRefresh = useRef(null)
  const [kickoffClient, setKickoffClient] = useState(null)
  const [workflowClient, setWorkflowClient] = useState(null)
  const [workflowOpen, setWorkflowOpen] = useState(false)
  const [showArchivedClients, setShowArchivedClients] = useState(false)
  const [fullClientProfileId, setFullClientProfileId] = useState(null)
  const [selectedSeoClientId, setSelectedSeoClientId] = useState(null)
  const [selectedSeoProjectId, setSelectedSeoProjectId] = useState(null)
  const [selectedWebsiteClientId, setSelectedWebsiteClientId] = useState(null)

  // Active clients (non-archived) — used in most views
  const clients = allClients.filter(c => !c.archived_at)
  // Clients shown in ClientsView depend on toggle
  const clientsForClientsView = showArchivedClients ? allClients : clients

  const handleTaskClick = (task) => {
    // Virtual website tasks route to the Website Workspace
    if (task._virtual && task._virtualTarget?.type === 'website') {
      setSelectedWebsiteClientId(task._virtualTarget.clientId)
      setView('website')
      return
    }
    // Virtual SEO tasks route to the SEO workspace
    if (task._virtual && task._virtualTarget?.clientId) {
      setSelectedSeoClientId(task._virtualTarget.clientId)
      setSelectedSeoProjectId(task._virtualTarget.projectId || null)
      setView('seo')
      return
    }
    if (task.blog_assignment_id) {
      setSelectedBlogId(task.blog_assignment_id)
      setSelectedBlogTaskId(task.id)
      setView('blogs')
      return
    }
    if (task.social_batch_id) {
      setSelectedBatchId(task.social_batch_id)
      setSelectedBatchTaskId(task.id)
      setView('social_batches')
      return
    }
    setDetailTask(task)
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => setSession(session))
    return () => subscription.unsubscribe()
  }, [])

  const loadAll = useCallback(async () => {
    if (!session) return
    const [{ data: ts }, { data: cs }, { data: ps }, { data: ns }, { data: seoProjects }, { data: seoYoastPages }] = await Promise.all([
      fetchAllActiveTasks(),
      supabase.from('clients').select('*').order('name'),
      supabase.from('profiles').select('*'),
      supabase.from('notifications').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false }).limit(50),
      supabase.from('seo_projects').select('*, items:seo_project_items(*)'),
      supabase.from('seo_pages').select('*'),
    ])
    // Synthesize virtual SEO tasks for the current user — they appear alongside real tasks
    // but don't exist in the DB.
    const myRole = (ps || []).find(p => p.id === session.user.id)?.role
    const virtualSeoTasks = computeVirtualSeoTasks(session.user.id, seoProjects || [], seoYoastPages || [], cs || [], myRole)
    const virtualWebsiteTasks = computeVirtualWebsiteTasks(session.user.id, seoYoastPages || [], cs || [])
    // Hide archived tasks from active views. They remain in the database
    // and are visible inside the client profile (which loads tasks directly).
    const activeTasks = (ts || []).filter(t => !t.archived_at)
    setTasks([...activeTasks, ...virtualSeoTasks, ...virtualWebsiteTasks])
    setAllClients(cs || [])
    setProfiles(ps || [])
    setNotifications(ns || [])
  }, [session])

  const loadNotifications = useCallback(async () => {
    if (!session) return
    const { data } = await supabase.from('notifications').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false }).limit(50)
    setNotifications(data || [])
  }, [session])

  useEffect(() => {
    if (!session) return
    supabase.from('profiles').select('*').eq('id', session.user.id).single().then(({ data }) => {
      if (data && !isActiveProfile(data)) {
        setDeactivatedNotice(true)
        supabase.auth.signOut()
        return
      }
      if (data) setProfile(data)
      else setShowProfileSetup(true)
    })
    loadAll()
    const channel = supabase.channel('notifs-' + session.user.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: 'user_id=eq.' + session.user.id }, () => loadNotifications())
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [session, loadAll, loadNotifications])

  const markNotifsRead = async (scope) => {
    // scope: 'messages' | 'other' — mark only that bucket read.
    const inScope = (n) => scope === 'messages' ? MESSAGE_TYPES.includes(n.type) : !MESSAGE_TYPES.includes(n.type)
    const ids = notifications.filter(n => !n.read && inScope(n)).map(n => n.id)
    if (ids.length === 0) return
    const { error } = await supabase.from('notifications').update({ read: true }).in('id', ids)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    setNotifications(ns => ns.map(n => ids.includes(n.id) ? { ...n, read: true } : n))
  }

  const exportBackup = async () => {
    const data = { tasks, clients: allClients, profiles, exportedAt: new Date().toISOString() }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `lmr-tasks-backup-${format(new Date(), 'yyyy-MM-dd')}.json`
    a.click()
    URL.revokeObjectURL(url)
    toast('Backup downloaded', 'success')
  }

  const handleDeleteTask = async () => {
    if (!deleteTask) return
    const { error } = await supabase.from('tasks').delete().eq('id', deleteTask.id)
    if (error) { toast('Error deleting task', 'error'); return }
    toast('Task deleted', 'success')
    setDeleteTask(null)
    setDetailTask(null)
    loadAll()
  }

  const handleTaskStatusChange = (taskId, newStatus) => {
    setTasks(ts => ts.map(t => t.id === taskId ? { ...t, status: newStatus } : t))
    if (detailTask?.id === taskId) setDetailTask(dt => ({ ...dt, status: newStatus }))
  }

  // Dashboard → All Tasks with a filter preset already applied.
  const openTasksWithFilters = (filters) => {
    setFilterClientId(null)
    setTasksPreset({ filters, token: Date.now() })
    setView('tasks')
  }

  // Must stay above the early returns below (rules of hooks).
  const blogsAwaitingClient = useMemo(() => {
    const ids = new Set()
    for (const t of tasks) {
      if (t.blog_phase !== 'client_review' && t.social_phase !== 'client_review') continue
      if (CLIENT_APPROVED_STATUSES.includes(t.status)) continue
      if (t.blog_assignment_id && t.blog_phase === 'client_review') ids.add('blog:' + t.blog_assignment_id)
      if (t.social_batch_id && t.social_phase === 'client_review') ids.add('social:' + t.social_batch_id)
    }
    return ids
  }, [tasks])

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>Loading…</div>
  if (!session) return <AuthPage deactivated={deactivatedNotice} />

  const currentUser = session.user
  const viewTasks = filterClientId ? tasks.filter(t => t.client_id === filterClientId) : tasks
  const unreadCount = notifications.filter(n => !n.read).length
  const pendingCount = tasks.filter(t => t.assigned_by === currentUser.id && ['ready_for_review', 'awaiting_client', 'goes_live', 'client_approved'].includes(t.status)).length

  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'tasks', icon: CheckSquare, label: 'All Tasks' },
    { id: 'approvals', icon: ClipboardCheck, label: 'Approvals', badge: pendingCount },
    { id: 'revisions', icon: GitMerge, label: 'Revisions', badge: tasks.filter(t => t.status === 'needs_revision' && (t.assigned_to === currentUser.id || (t.collaborator_ids||[]).includes(currentUser.id))).length },
    { id: 'assigned', icon: UserCheck, label: 'Assigned by Me' },
    { id: 'time', icon: Clock, label: 'My Time' },
    ...(profile?.role === 'supervising_ad' ? [{ id: 'time_report', icon: BarChart3, label: 'Time Report' }] : []),
    ...(canViewCommission(currentUser.id) ? [{ id: 'commission', icon: DollarSign, label: 'Commission' }] : []),
    ...(canViewADScorecard(currentUser.id, profile) ? [{ id: 'ad_scorecard', icon: TrendingUp, label: 'AD Scorecards' }] : []),
    { id: 'first90', icon: Sprout, label: 'First 90 Days' },
    { id: 'kickoff_decks', icon: Mic, label: 'Kickoff Decks' },
    { id: 'meetings', icon: NotebookPen, label: 'Meeting Notes' },
    { id: 'blogs', icon: PenLine, label: 'Blog Assignments' },
    { id: 'social_batches', icon: Smartphone, label: 'Social Batches' },
    { id: 'seo', icon: Search, label: 'SEO Workspace' },
    { id: 'website', icon: Globe, label: 'Website Workspace' },
    { id: 'recurring', icon: RefreshCw, label: 'Recurring' },
    { id: 'projects', icon: Layers, label: 'Projects' },
    { id: 'onboarding', icon: Star, label: 'New Clients' },
    { id: 'clients', icon: Building2, label: 'Clients' },
    { id: 'inventory', icon: ClipboardList, label: 'Inventory' },
    { id: 'resources', icon: BookOpen, label: 'Resources' },
    { id: 'team', icon: Users, label: 'Team' },
    { id: 'tickets', icon: AlertTriangle, label: 'Ticket Desk' },
    { id: 'admin', icon: Shield, label: 'Admin' },
  ]

  const titles = {
    dashboard: 'Dashboard', tasks: 'All Tasks', approvals: 'Approvals',
    meetings: 'Meeting Notes', blogs: 'Blog Assignments', social_batches: 'Social Batches', seo: 'SEO Workspace', website: 'Website Workspace', recurring: 'Recurring Tasks', projects: 'Projects', onboarding: 'New Clients', clients: 'Clients', inventory: 'Client Inventory', team: 'Team', revisions: 'Revisions', assigned: 'Assigned by Me', time: 'My Time', time_report: 'Time Report', commission: 'Commission', ad_scorecard: 'AD Scorecards', first90: 'First 90 Days', kickoff_decks: 'Kickoff Decks', resources: 'Resources', admin: 'Admin'
  }

  return (
    <TimerProvider currentUser={currentUser} profile={profile} allClients={allClients} tasks={tasks}>
    <BlogsAwaitingClientContext.Provider value={blogsAwaitingClient}>
    <div className="app">
      <ActiveTimerBar />
      <RunawayTimerReminder />
      <nav className="sidebar">
        <div className="sidebar-logo"><h1>LMR</h1><p>Task Manager V2</p></div>
        <div className="sidebar-section">Navigation</div>
        {navItems.map(({ id, icon: Icon, label, badge }) => (
          <button key={id} className={`sidebar-item ${view === id && !filterClientId ? 'active' : ''}`}
            onClick={() => { setView(id); setFilterClientId(null) }}
            style={id === 'tasks' ? { fontWeight: 700, fontSize: 14 } : undefined}>
            <Icon className="icon" />
            {label}
            {badge > 0 && <span style={{ marginLeft: 'auto', background: 'var(--gold)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>{badge}</span>}
          </button>
        ))}

        {allProjects.filter(p => p.status === 'active').length > 0 && <>
          <div className="sidebar-section">Projects</div>
          {allProjects.filter(p => p.status === 'active').map(p => (
            <button key={p.id} className={`sidebar-item ${view === 'projects' && selectedProjectId === p.id ? 'active' : ''}`}
              onClick={() => { setView('projects'); setSelectedProjectId(p.id); setFilterClientId(null) }}>
              <Layers className="icon" />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
            </button>
          ))}
        </>}

        {clients.length > 0 && <>
          <div className="sidebar-section">Clients</div>
          {clients.map(c => (
            <button key={c.id} className={`sidebar-item ${view === 'client_profile' && fullClientProfileId === c.id ? 'active' : ''}`}
              onClick={() => { setFullClientProfileId(c.id); setView('client_profile'); setFilterClientId(null) }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.is_onboarding && !c.onboarding_complete ? 'var(--gold)' : 'var(--teal)', flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
            </button>
          ))}
        </>}

        <div className="sidebar-bottom">
          <div className="user-pill">
            <div className="avatar" style={{ background: 'var(--navy-mid)' }}>
              {(profile?.full_name || currentUser.email || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="user-info">
              <div className="user-name">{profile?.full_name || currentUser.email}</div>
              <div className="user-role">{ROLES.find(r => r.value === profile?.role)?.label || 'Team'}</div>
            </div>
          </div>
          <button className="sidebar-item" onClick={() => supabase.auth.signOut()}>
            <LogOut className="icon" />Sign Out
          </button>
        </div>
      </nav>

      <div className="main">
        <div className="topbar">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div className="topbar-title">{filterClientId ? allClients.find(c => c.id === filterClientId)?.name : titles[view]}</div>
              {filterClientId && (
                <button onClick={() => { setFullClientProfileId(filterClientId); setView('client_profile'); setFilterClientId(null) }}
                  style={{ background: 'var(--cream-dark)', border: '1px solid var(--border)', color: 'var(--navy)', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                  title="View full client profile">
                  📋 Full Client Profile
                </button>
              )}
            </div>
            {filterClientId && <button style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 4 }} onClick={() => setFilterClientId(null)}>← Back to all tasks</button>}
          </div>
          <div className="topbar-actions">
            <button className="btn-icon" onClick={exportBackup} title="Export backup"><Download size={15} /></button>
            <div className="notif-wrap" style={{ position: 'relative' }}>
              <button className="btn-icon" onClick={() => setShowNotifs(s => !s)} title="Messages & notifications">
                <MessageSquare size={16} color={unreadCount > 0 ? 'var(--gold)' : undefined} fill={unreadCount > 0 ? 'var(--gold)' : 'none'} />
              </button>
              {showNotifs && <NotifPanel notifications={notifications} onMarkRead={markNotifsRead} tasks={tasks} onTaskClick={t => {
                if (t.blog_assignment_id) { setSelectedBlogId(t.blog_assignment_id); setSelectedBlogTaskId(t.id); setView('blogs'); setShowNotifs(false) }
                else if (t.social_batch_id) { setSelectedBatchId(t.social_batch_id); setSelectedBatchTaskId(t.id); setView('social_batches'); setShowNotifs(false) }
                else { setDetailTask(t); setShowNotifs(false) }
              }} />}
            </div>
            <button className="btn btn-ghost" onClick={() => { setWorkflowClient(null); setWorkflowOpen(true) }} title="Run a workflow">
              ⚡ Run Workflow
            </button>
            <button className="btn btn-ghost" onClick={() => setShowBulkModal(true)} title="Bulk add tasks">
              <Plus size={15} /> Bulk Add
            </button>
            <button className="btn btn-gold" onClick={() => { setEditTask(null); setShowTaskModal(true) }}>
              <Plus size={15} /> New Task
            </button>
          </div>
        </div>

        <div className="content">
          {view === 'dashboard' && <DashboardView tasks={tasks} clients={clients} profiles={profiles} currentUser={currentUser} onTaskClick={handleTaskClick} onOpenTasks={openTasksWithFilters} />}
          {view === 'tasks' && <TasksView tasks={viewTasks} profiles={profiles} clients={clients} currentUser={currentUser} onTaskClick={handleTaskClick} onDeleteTask={t => setDeleteTask(t)} forceGroupBy={filterClientId ? 'none' : null} preset={tasksPreset} onPresetApplied={() => setTasksPreset(null)} />}
          {view === 'approvals' && <ApprovalsView tasks={tasks} profiles={profiles} clients={clients} currentUser={currentUser} onTaskClick={handleTaskClick} onDeleteTask={t => setDeleteTask(t)} />}
          {view === 'onboarding' && <OnboardingView clients={clients} tasks={tasks} onClientClick={id => { setFilterClientId(id); setView('tasks') }} onRunKickoff={c => setKickoffClient(c)} />}
          {view === 'clients' && <ClientsView clients={clientsForClientsView} allClients={allClients} tasks={tasks} onAddClient={() => setShowAddClient(true)} onClientClick={id => { setFilterClientId(id); setView('tasks') }} onViewProfile={c => setViewingClientProfile(c)} showArchived={showArchivedClients} setShowArchived={setShowArchivedClients} />}
          {view === 'inventory' && <InventoryView clients={allClients} profiles={profiles} tasks={tasks} currentUser={currentUser} onOpenClient={c => { setFullClientProfileId(c.id); setView('client_profile') }} onAddClient={() => setShowAddClient(true)} />}
          {view === 'meetings' && <MeetingNotesView clients={clients} profiles={profiles} currentUser={currentUser} selectedNote={selectedNote} onClearNote={() => setSelectedNote(null)} />}
          {view === 'blogs' && <BlogAssignmentsView clients={clients} profiles={profiles} currentUser={currentUser} profile={profile} selectedBlogId={selectedBlogId} selectedBlogTaskId={selectedBlogTaskId} onClearBlogId={() => { setSelectedBlogId(null); setSelectedBlogTaskId(null) }} onOpenClientProfile={id => { setFullClientProfileId(id); setView('client_profile') }} />}
          {view === 'social_batches' && <SocialBatchesView clients={clients} profiles={profiles} currentUser={currentUser} profile={profile} selectedBatchId={selectedBatchId} selectedBatchTaskId={selectedBatchTaskId} onClearBatchId={() => { setSelectedBatchId(null); setSelectedBatchTaskId(null) }} onOpenClientProfile={id => { setFullClientProfileId(id); setView('client_profile') }} />}
          {view === 'seo' && <SEOWorkspaceView clients={clients} profiles={profiles} currentUser={currentUser} profile={profile} selectedSeoClientId={selectedSeoClientId} selectedSeoProjectId={selectedSeoProjectId} onClearSeoClientId={() => { setSelectedSeoClientId(null); setSelectedSeoProjectId(null) }} onOpenTask={t => setDetailTask(tasks.find(x => x.id === t.id) || t)} />}
          {view === 'website' && <WebsiteWorkspaceView clients={clients} profiles={profiles} currentUser={currentUser} profile={profile} selectedWebsiteClientId={selectedWebsiteClientId} onClearWebsiteClientId={() => setSelectedWebsiteClientId(null)} onOpenTask={t => setDetailTask(tasks.find(x => x.id === t.id) || t)} />}
          {view === 'client_profile' && <ClientProfileFull
            clientId={fullClientProfileId}
            clients={allClients}
            profiles={profiles}
            currentUser={currentUser}
            onBack={() => { setFullClientProfileId(null); setView('clients') }}
            onOpenBlog={blog => { setSelectedBlogId(blog.id); setView('blogs') }}
            onOpenBatch={batch => { setSelectedBatchId(batch.id); setView('social_batches') }}
            onOpenSeo={id => { setSelectedSeoClientId(id); setView('seo') }}
            onOpenWebsite={id => { setSelectedWebsiteClientId(id); setView('website') }}
            onOpenNote={note => { setSelectedNote(note); setFullClientProfileId(null); setView('meetings') }}
            onOpenTask={(task, refresh) => { detailRefresh.current = refresh || null; setDetailTask(task) }}
            onEdit={() => { const c = allClients.find(x => x.id === fullClientProfileId); if (c) setViewingClientProfile(c) }}
            onReload={loadAll}
            onRunWorkflow={c => { setWorkflowClient(c); setWorkflowOpen(true) }}
          />}
          {view === 'recurring' && <RecurringTasksView clients={clients} profiles={profiles} currentUser={currentUser} onNewTask={() => { setEditTask(null); setShowTaskModal(true) }} />}
          {view === 'projects' && <ProjectsView clients={clients} profiles={profiles} currentUser={currentUser} profile={profile} selectedProjectId={selectedProjectId} onProjectsLoaded={setAllProjects} onTaskClick={handleTaskClick} onOpenSeoProject={(clientId, projectId) => { setSelectedSeoClientId(clientId); setSelectedSeoProjectId(projectId || null); setView('seo') }} />}
          {view === 'team' && <TeamView profiles={profiles} tasks={tasks} />}
          {view === 'revisions' && <RevisionsView tasks={tasks} profiles={profiles} clients={clients} currentUser={currentUser} onTaskClick={handleTaskClick} onDeleteTask={t => setDeleteTask(t)} />}
          {view === 'assigned' && <AssignedByMeView tasks={tasks} profiles={profiles} clients={clients} currentUser={currentUser} onTaskClick={handleTaskClick} onDeleteTask={t => setDeleteTask(t)} />}
          {view === 'time' && <TimeTrackingView allClients={allClients} tasks={tasks} currentUser={currentUser} profile={profile} />}
          {view === 'time_report' && profile?.role === 'supervising_ad' && <TimeReportView profiles={profiles} allClients={allClients} profile={profile} />}
          {view === 'commission' && canViewCommission(currentUser.id) && <CommissionView allClients={allClients} currentUser={currentUser} />}
          {view === 'ad_scorecard' && canViewADScorecard(currentUser.id, profile) && <ADScorecardView profiles={profiles} allClients={allClients} currentUser={currentUser} profile={profile} />}
          {view === 'first90' && <First90View allClients={allClients} tasks={tasks} profiles={profiles} currentUser={currentUser} onOpenTasks={openTasksWithFilters} />}
          {view === 'kickoff_decks' && <KickoffDecksView allClients={allClients} profiles={profiles} currentUser={currentUser} onClientsChanged={loadAll} />}
          {view === 'resources' && <ResourcesView />}
          {view === 'admin' && <AdminView profiles={profiles} tasks={tasks} currentUser={currentUser} profile={profile} onReload={loadAll} />}
          {view === 'tickets' && <TicketDeskView clients={clients} profiles={profiles} currentUser={currentUser} profile={profile} />}
        </div>
      </div>

      {showTaskModal && <TaskModal task={editTask} clients={clients} profiles={profiles} currentUser={currentUser} onClose={() => { setShowTaskModal(false); setEditTask(null) }} onSaved={() => { setShowTaskModal(false); setEditTask(null); loadAll() }} />}
      {detailTask && <TaskDetail task={detailTask} profiles={profiles} clients={clients} currentUser={currentUser} onClose={() => { setDetailTask(null); detailRefresh.current = null }} onEdit={t => { setDetailTask(null); setEditTask(t); setShowTaskModal(true) }} onStatusChange={(...a) => { handleTaskStatusChange(...a); if (detailRefresh.current) detailRefresh.current() }} onViewClientProfile={c => setViewingClientProfile(c)} onTaskUpdated={(updated) => { setDetailTask(prev => ({ ...prev, ...updated })); setTasks(ts => ts.map(t => t.id === updated.id ? { ...t, ...updated } : t)); if (detailRefresh.current) detailRefresh.current() }}  onClientArchived={() => { setDetailTask(null); detailRefresh.current = null; loadAll() }} />}
      {showAddClient && <AddClientModal
        onClose={() => setShowAddClient(false)}
        onSaved={() => { setShowAddClient(false); loadAll() }}
        toast={toast}
        currentUser={currentUser}
        profiles={profiles}
      />}
      {deleteTask && <DeleteConfirmModal task={deleteTask} onClose={() => setDeleteTask(null)} onConfirm={handleDeleteTask} />}
      {viewingClientProfile && <ClientProfile
        client={viewingClientProfile}
        profiles={profiles}
        tasks={tasks}
        currentUser={currentUser}
        onClose={() => setViewingClientProfile(null)}
        onUpdated={() => { setViewingClientProfile(null); loadAll() }}
        onOpenFull={() => { setFullClientProfileId(viewingClientProfile.id); setViewingClientProfile(null); setView('client_profile') }}
      />}
      {showBulkModal && <BulkTaskModal clients={clients} profiles={profiles} currentUser={currentUser} onClose={() => setShowBulkModal(false)} onSaved={() => { setShowBulkModal(false); loadAll() }} />}
      {showProfileSetup && profile === null && <SetupProfileModal user={currentUser} toast={toast} onSaved={() => { setShowProfileSetup(false); supabase.from('profiles').select('*').eq('id', currentUser.id).single().then(({ data }) => setProfile(data)) }} />}
      {kickoffClient && <KickoffBuilder
        client={kickoffClient}
        currentUser={currentUser}
        profiles={profiles}
        onClose={() => setKickoffClient(null)}
        onSaved={() => { setKickoffClient(null); loadAll() }}
      />}
      {workflowOpen && <WorkflowBuilder
        client={workflowClient}
        clients={clients}
        currentUser={currentUser}
        profiles={profiles}
        onClose={() => { setWorkflowOpen(false); setWorkflowClient(null) }}
        onSaved={() => { setWorkflowOpen(false); setWorkflowClient(null); loadAll() }}
      />}
    </div>
    </BlogsAwaitingClientContext.Provider>
    </TimerProvider>
  )
}